import React, { useState, useEffect } from 'react';
import {
  Box,
  Header,
  SpaceBetween,
  Table,
  Pagination,
  Button,
  PropertyFilter,
  StatusIndicator,
  CollectionPreferences,
  CollectionPreferencesProps,
  ButtonDropdown
} from '@cloudscape-design/components';
import { useHelpPanel } from '../contexts/HelpContext';
import { InfoLink } from '../components/help/common';
import { NotificationMonitoringHelp } from '../components/help/NotificationMonitoringHelp';
import { notificationsApi } from '../services/api';

interface NotificationMonitoringProps {
  addNotification: (notification: any) => void;
}

interface NotificationItem {
  notificationId: number;
  eventId: number;
  eventType: number;
  eventName: string;
  eventDate: string;
  customerName: string;
  awsAccountId: string;
  accountManagerEmail: string;
  accountManagerAlias: string;
  accountManagerFullName: string;
  solutionId: string;
  solutionResponseName: string;
  status: 'PENDING' | 'SENT' | 'READ' | 'ESCALATED';
  createdAt: string;
  updatedAt: string;
  emailTracking?: {
    trackingId: string;
    messageId: string;
    recipientEmail: string;
    subject: string;
    sentTimestamp: string;
    openTimestamp?: string;
    clickTimestamp?: string;
    openCount: number;
    clickCount: number;
    status: string;
  };
  slackNotification?: {
    slackNotificationId: number;
    accountManagerAlias: string;
    message: string;
    sentTimestamp?: string;
    status: string;
  };
}

const NotificationMonitoring: React.FC<NotificationMonitoringProps> = ({ addNotification }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { setContent } = useHelpPanel();
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [filteringProperties, setFilteringProperties] = useState<any>({ tokens: [], operation: 'and' });
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    visibleContent: ['notificationId', 'eventName', 'customerName', 'solutionId', 'solutionName', 'accountManager', 'createdAt', 'status', 'emailStats']
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Load notifications from API
  const loadNotifications = async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Build filters from PropertyFilter tokens
      const filters: any = {};
      
      filteringProperties.tokens.forEach((token: any) => {
        const { propertyKey, operator, value } = token;
        
        if (propertyKey === 'status') {
          if (!filters.status) filters.status = [];
          if (operator === '=' && !filters.status.includes(value)) {
            filters.status.push(value);
          }
        } else if (propertyKey === 'eventName') {
          if (!filters.eventType) filters.eventType = [];
          if (operator === '=' && !filters.eventType.includes(value)) {
            filters.eventType.push(value);
          }
        } else if (propertyKey === 'customerName' && operator === ':') {
          filters.customerName = value;
        } else if (propertyKey === 'accountManager' && operator === ':') {
          filters.accountManager = value;
        }
      });

      const params = {
        page: currentPageIndex,
        perPage: preferences.pageSize || 10,
        sortField: 'created_at',
        sortDirection: 'DESC' as const,
        ...filters
      };

      const response = await notificationsApi.getAll(params);
      
      if (response.data) {
        setNotifications(response.data.notifications || []);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      addNotification({
        type: 'error',
        content: 'Failed to load notifications. Please try again.',
        dismissible: true,
        dismissTimeout: 5000
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load notifications on component mount and when filters/pagination change
  useEffect(() => {
    loadNotifications();
  }, [currentPageIndex, filteringProperties, preferences.pageSize]);

  // Notifications are already filtered and paginated by the API
  const paginatedItems = notifications;

  // Function to get status type for the StatusIndicator component
  const getStatusType = (status: string): "success" | "warning" | "error" | "info" | "pending" => {
    switch (status.toUpperCase()) {
      case 'READ':
        return 'success';
      case 'SENT':
        return 'info';
      case 'ESCALATED':
        return 'warning';
      case 'PENDING':
        return 'pending';
      default:
        return 'info';
    }
  };

  // Action to resend a notification
  const handleResendNotification = async (notificationId: number) => {
    try {
      await notificationsApi.resend(notificationId);
      addNotification({
        type: 'success',
        content: `Notification ${notificationId} has been queued for resending.`,
        dismissible: true,
        dismissTimeout: 5000
      });
      // Refresh the data
      loadNotifications(true);
    } catch (error) {
      addNotification({
        type: 'error',
        content: `Failed to resend notification ${notificationId}.`,
        dismissible: true,
        dismissTimeout: 5000
      });
    }
  };

  // Action to mark as escalated
  const handleEscalateNotification = async (notificationId: number) => {
    try {
      await notificationsApi.escalate(notificationId);
      addNotification({
        type: 'success',
        content: `Notification ${notificationId} has been escalated.`,
        dismissible: true,
        dismissTimeout: 5000
      });
      // Refresh the data
      loadNotifications(true);
    } catch (error) {
      addNotification({
        type: 'error',
        content: `Failed to escalate notification ${notificationId}.`,
        dismissible: true,
        dismissTimeout: 5000
      });
    }
  };

  // Bulk resend notifications
  const handleBulkResend = async () => {
    if (selectedItems.length === 0) return;
    
    try {
      const notificationIds = selectedItems.map(item => item.notificationId);
      await notificationsApi.bulkResend(notificationIds);
      addNotification({
        type: 'success',
        content: `${selectedItems.length} notifications have been queued for resending.`,
        dismissible: true,
        dismissTimeout: 5000
      });
      setSelectedItems([]);
      loadNotifications(true);
    } catch (error) {
      addNotification({
        type: 'error',
        content: 'Failed to resend selected notifications.',
        dismissible: true,
        dismissTimeout: 5000
      });
    }
  };

  // Bulk escalate notifications
  const handleBulkEscalate = async () => {
    if (selectedItems.length === 0) return;
    
    try {
      const notificationIds = selectedItems.map(item => item.notificationId);
      await notificationsApi.bulkEscalate(notificationIds);
      addNotification({
        type: 'success',
        content: `${selectedItems.length} notifications have been escalated.`,
        dismissible: true,
        dismissTimeout: 5000
      });
      setSelectedItems([]);
      loadNotifications(true);
    } catch (error) {
      addNotification({
        type: 'error',
        content: 'Failed to escalate selected notifications.',
        dismissible: true,
        dismissTimeout: 5000
      });
    }
  };

  // Export notifications to CSV
  const handleExportToCSV = async () => {
    setExporting(true);
    try {
      // Fetch all notifications with current filters (not just current page)
      const filters: any = {};
      
      filteringProperties.tokens.forEach((token: any) => {
        const { propertyKey, operator, value } = token;
        
        if (propertyKey === 'status') {
          if (!filters.status) filters.status = [];
          if (operator === '=' && !filters.status.includes(value)) {
            filters.status.push(value);
          }
        } else if (propertyKey === 'eventName') {
          if (!filters.eventType) filters.eventType = [];
          if (operator === '=' && !filters.eventType.includes(value)) {
            filters.eventType.push(value);
          }
        } else if (propertyKey === 'customerName' && operator === ':') {
          filters.customerName = value;
        } else if (propertyKey === 'accountManager' && operator === ':') {
          filters.accountManager = value;
        }
      });

      // Fetch all notifications (up to 10000 records)
      const params = {
        page: 1,
        perPage: 10000,
        sortField: 'created_at',
        sortDirection: 'DESC' as const,
        ...filters
      };

      const response = await notificationsApi.getAll(params);
      const allNotifications = response.data?.notifications || [];

      if (allNotifications.length === 0) {
        addNotification({
          type: 'warning',
          content: 'No notifications to export.',
          dismissible: true,
          dismissTimeout: 5000
        });
        return;
      }

      // Define CSV headers
      const headers = [
        'Notification ID',
        'Event Type',
        'Customer Name',
        'Solution ID',
        'Solution Name',
        'Account Manager',
        'Account Manager Email',
        'Created Date',
        'Status',
        'Email Opens',
        'Email Clicks',
        'Email Sent',
        'Email Opened',
        'Slack Notification Sent'
      ];

      // Convert notifications to CSV rows
      const rows = allNotifications.map((item: NotificationItem) => [
        item.notificationId,
        item.eventName,
        item.customerName,
        item.solutionId || '',
        item.solutionResponseName || '',
        item.accountManagerFullName || item.accountManagerEmail,
        item.accountManagerEmail,
        new Date(item.createdAt).toLocaleString(),
        item.status,
        item.emailTracking?.openCount || 0,
        item.emailTracking?.clickCount || 0,
        item.emailTracking?.sentTimestamp ? new Date(item.emailTracking.sentTimestamp).toLocaleString() : '',
        item.emailTracking?.openTimestamp ? new Date(item.emailTracking.openTimestamp).toLocaleString() : '',
        item.slackNotification?.sentTimestamp ? new Date(item.slackNotification.sentTimestamp).toLocaleString() : ''
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map((row: (string | number)[]) => row.map((cell: string | number) => {
          // Escape cells that contain commas, quotes, or newlines
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `notifications_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification({
        type: 'success',
        content: `Exported ${allNotifications.length} notifications to CSV.`,
        dismissible: true,
        dismissTimeout: 5000
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      addNotification({
        type: 'error',
        content: 'Failed to export notifications to CSV.',
        dismissible: true,
        dismissTimeout: 5000
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <Header
          variant="h1"
          info={<InfoLink onFollow={() => setContent(<NotificationMonitoringHelp />)} />}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                iconName="download" 
                onClick={handleExportToCSV}
                disabled={totalItems === 0}
                loading={exporting}
                loadingText="Exporting..."
              >
                Export to CSV
              </Button>
              <Button 
                iconName="refresh" 
                loading={refreshing}
                onClick={() => loadNotifications(true)}
              >
                Refresh Data
              </Button>
            </SpaceBetween>
          }
        >
          Notification Monitoring
        </Header>

        <PropertyFilter
          filteringProperties={[
            {
              key: 'eventName',
              operators: ['=', '!='],
              propertyLabel: 'Event Type',
              groupValuesLabel: 'Event Type values'
            },
            {
              key: 'customerName',
              operators: [':', '!:'],
              propertyLabel: 'Customer Name',
              groupValuesLabel: 'Customer values'
            },
            {
              key: 'accountManager',
              operators: [':', '!:'],
              propertyLabel: 'Account Manager',
              groupValuesLabel: 'Account Manager values'
            },
            {
              key: 'status',
              operators: ['=', '!='],
              propertyLabel: 'Status',
              groupValuesLabel: 'Status values'
            }
          ]}
          query={filteringProperties}
          onChange={({ detail }) => setFilteringProperties(detail)}
          countText={`${totalItems} matches`}
          expandToViewport
        />

        <Table
          columnDefinitions={[
            {
              id: 'notificationId',
              header: 'ID',
              cell: item => item.notificationId,
              sortingField: 'notificationId'
            },
            {
              id: 'eventName',
              header: 'Event Type',
              cell: item => item.eventName,
              sortingField: 'eventName'
            },
            {
              id: 'customerName',
              header: 'Customer',
              cell: item => item.customerName,
              sortingField: 'customerName'
            },
            {
              id: 'solutionId',
              header: 'Solution ID',
              cell: item => item.solutionId,
              sortingField: 'solutionId'
            },
            {
              id: 'solutionName',
              header: 'Solution Name',
              cell: item => item.solutionResponseName,
              sortingField: 'solutionResponseName'
            },
            {
              id: 'accountManager',
              header: 'Account Manager',
              cell: item => item.accountManagerFullName || item.accountManagerEmail,
              sortingField: 'accountManagerEmail'
            },
            {
              id: 'createdAt',
              header: 'Created Date',
              cell: item => new Date(item.createdAt).toLocaleString(),
              sortingField: 'createdAt'
            },
            {
              id: 'status',
              header: 'Status',
              cell: item => (
                <StatusIndicator type={getStatusType(item.status)}>
                  {item.status}
                </StatusIndicator>
              ),
              sortingField: 'status'
            },
            {
              id: 'emailStats',
              header: 'Email Stats',
              cell: item => {
                const opens = item.emailTracking?.openCount || 0;
                const clicks = item.emailTracking?.clickCount || 0;
                return `${opens} opens, ${clicks} clicks`;
              },
              sortingField: 'emailStats'
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: item => (
                <ButtonDropdown
                  variant="inline-icon"
                  ariaLabel={`Notification ${item.notificationId} actions`}
                  expandToViewport={true}
                  items={[
                    { id: 'resend', text: 'Resend notification' },
                    { id: 'escalate', text: 'Mark for escalation' },
                    { id: 'view', text: 'View details' }
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === 'resend') handleResendNotification(item.notificationId);
                    if (detail.id === 'escalate') handleEscalateNotification(item.notificationId);
                  }}
                />
              )
            }
          ]}
          items={paginatedItems}
          loading={loading}
          loadingText="Loading notifications"
          selectionType="multi"
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={{
                title: "Page size",
                options: [
                  { value: 10, label: "10 notifications" },
                  { value: 20, label: "20 notifications" },
                  { value: 50, label: "50 notifications" }
                ]
              }}
              visibleContentPreference={{
                title: "Select visible columns",
                options: [
                  {
                    label: "Notification properties",
                    options: [
                      { id: "notificationId", label: "ID" },
                      { id: "eventName", label: "Event Type" },
                      { id: "customerName", label: "Customer" },
                      { id: "solutionId", label: "Solution ID" },
                      { id: "solutionName", label: "Solution Name" },
                      { id: "accountManager", label: "Account Manager" },
                      { id: "createdAt", label: "Created Date" },
                      { id: "status", label: "Status" },
                      { id: "emailStats", label: "Email Stats" }
                    ]
                  }
                ]
              }}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>No notifications</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No notifications match the current filter criteria.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={selectedItems.length ? `(${selectedItems.length}/${totalItems})` : `(${totalItems})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button 
                    disabled={selectedItems.length === 0}
                    iconName="envelope"
                    onClick={handleBulkResend}
                  >
                    Resend selected
                  </Button>
                  <Button 
                    disabled={selectedItems.length === 0}
                    iconName="status-warning"
                    variant="primary"
                    onClick={handleBulkEscalate}
                  >
                    Escalate selected
                  </Button>
                </SpaceBetween>
              }
            >
              Notifications
            </Header>
          }
        />
      </SpaceBetween>
    </Box>
  );
};

export default NotificationMonitoring;
